import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, Users, Calendar, Package, Building2, Briefcase, Sparkles } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { searchClients } from '../services/clientApi';
import { searchProperties } from '../services/propertyApi';
import { searchEmployees } from '../services/employeeApi';
import { searchServices } from '../services/serviceApi';
import { searchProducts } from '../services/productApi';
import { getAppointments } from '../services/appointmentApi';

export type SearchScope = 'all' | 'clients' | 'appointments' | 'services' | 'products' | 'properties' | 'employees';

interface SearchResult {
  id: string;
  type: SearchScope;
  title: string;
  subtitle?: string;
  metadata?: Record<string, any>;
}

interface GlobalSearchProps {
  onResultClick?: (result: SearchResult) => void;
  placeholder?: string;
  openTrigger?: number; // Quando mudar, abre o search
}

const STORAGE_KEY = 'housekeeper_search_history';
const MAX_HISTORY_ITEMS = 10;

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onResultClick, placeholder, openTrigger }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedScope, setSelectedScope] = useState<SearchScope>('all');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Abrir search quando openTrigger mudar
  useEffect(() => {
    if (openTrigger !== undefined && openTrigger > 0) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  }, [openTrigger]);

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar histórico ao montar
  useEffect(() => {
    const history = localStorage.getItem(STORAGE_KEY);
    if (history) {
      try {
        const parsed = JSON.parse(history);
        setSearchHistory(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error('Erro ao carregar histórico de buscas:', e);
      }
    }
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar sugestões enquanto digita
  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      const timer = setTimeout(() => {
        generateSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm]);

  const generateSuggestions = async (term: string) => {
    // Gerar sugestões baseadas no histórico e termo atual
    const historyMatches = searchHistory
      .filter(h => h.toLowerCase().includes(term.toLowerCase()))
      .slice(0, 5);
    
    setSuggestions(historyMatches);
    setShowSuggestions(historyMatches.length > 0);
  };

  const performSearch = async (term: string, scope: SearchScope) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    const searchResults: SearchResult[] = [];

    try {
      // Buscar em paralelo baseado no escopo
      const searchPromises: Promise<any>[] = [];

      if (scope === 'all' || scope === 'clients') {
        searchPromises.push(
          searchClients(term).then(clients =>
            clients.map((client: any) => ({
              id: client.id,
              type: 'clients' as SearchScope,
              title: client.name,
              subtitle: client.phone || client.document,
              metadata: client,
            }))
          ).catch(() => [])
        );
      }

      if (scope === 'all' || scope === 'properties') {
        searchPromises.push(
          searchProperties(term).then(properties =>
            properties.map((property: any) => ({
              id: property.id,
              type: 'properties' as SearchScope,
              title: property.name,
              subtitle: property.address,
              metadata: property,
            }))
          ).catch(() => [])
        );
      }

      if (scope === 'all' || scope === 'employees') {
        searchPromises.push(
          searchEmployees(term).then(employees =>
            employees.map((employee: any) => ({
              id: employee.id,
              type: 'employees' as SearchScope,
              title: employee.name,
              subtitle: employee.email,
              metadata: employee,
            }))
          ).catch(() => [])
        );
      }

      if (scope === 'all' || scope === 'services') {
        searchPromises.push(
          searchServices(term).then(services =>
            services.map((service: any) => ({
              id: service.id,
              type: 'services' as SearchScope,
              title: service.name,
              subtitle: service.description,
              metadata: service,
            }))
          ).catch(() => [])
        );
      }

      if (scope === 'all' || scope === 'products') {
        searchPromises.push(
          searchProducts(term).then(products =>
            products.map((product: any) => ({
              id: product.id,
              type: 'products' as SearchScope,
              title: product.name,
              subtitle: product.description || product.category,
              metadata: product,
            }))
          ).catch(() => [])
        );
      }

      if (scope === 'all' || scope === 'appointments') {
        // Buscar agendamentos - buscar todos e filtrar localmente
        searchPromises.push(
          getAppointments().then((response: any) => {
            // A resposta pode vir em diferentes formatos
            let appointments: any[] = [];
            if (Array.isArray(response)) {
              appointments = response;
            } else if (response && typeof response === 'object') {
              appointments = response.appointments || response.data || response.results || [];
            }
            
            const searchLower = term.toLowerCase();
            return appointments
              .filter((apt: any) => {
                // Buscar em múltiplos campos
                return (
                  apt.id?.toLowerCase().includes(searchLower) ||
                  apt.property_id?.toLowerCase().includes(searchLower) ||
                  apt.employee_id?.toLowerCase().includes(searchLower) ||
                  apt.special_instructions?.toLowerCase().includes(searchLower) ||
                  apt.status?.toLowerCase().includes(searchLower) ||
                  apt.scheduled_date?.toLowerCase().includes(searchLower)
                );
              })
              .slice(0, 10)
              .map((apt: any) => {
                const dateStr = apt.scheduled_date 
                  ? new Date(apt.scheduled_date).toLocaleDateString('pt-BR')
                  : 'Data não informada';
                return {
                  id: apt.id,
                  type: 'appointments' as SearchScope,
                  title: `Agendamento - ${dateStr}`,
                  subtitle: apt.special_instructions || `Status: ${apt.status || 'N/A'}`,
                  metadata: apt,
                };
              });
          }).catch(() => [])
        );
      }

      const resultsArrays = await Promise.all(searchPromises);
      resultsArrays.forEach(arr => searchResults.push(...arr));

      setResults(searchResults);
      setIsOpen(true);

      // Salvar no histórico
      if (term.trim() && !searchHistory.includes(term.trim())) {
        const newHistory = [term.trim(), ...searchHistory].slice(0, MAX_HISTORY_ITEMS);
        setSearchHistory(newHistory);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    performSearch(term, selectedScope);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim().length >= 2) {
      handleSearch(value);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(searchTerm);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setShowSuggestions(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (onResultClick) {
      onResultClick(result);
    }
    setIsOpen(false);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    handleSearch(suggestion);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
    setShowSuggestions(false);
  };

  const getScopeIcon = (scope: SearchScope) => {
    switch (scope) {
      case 'clients':
        return <Users size={16} />;
      case 'appointments':
        return <Calendar size={16} />;
      case 'services':
        return <Sparkles size={16} />;
      case 'products':
        return <Package size={16} />;
      case 'properties':
        return <Building2 size={16} />;
      case 'employees':
        return <Briefcase size={16} />;
      default:
        return <Search size={16} />;
    }
  };

  const getResultIcon = (type: SearchScope) => {
    return getScopeIcon(type);
  };

  const scopeOptions: { value: SearchScope; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: t('searchAll'), icon: <Search size={14} /> },
    { value: 'clients', label: t('clients'), icon: <Users size={14} /> },
    { value: 'appointments', label: t('appointments'), icon: <Calendar size={14} /> },
    { value: 'services', label: t('menu.services'), icon: <Sparkles size={14} /> },
    { value: 'products', label: t('products'), icon: <Package size={14} /> },
    { value: 'properties', label: t('properties'), icon: <Building2 size={14} /> },
    { value: 'employees', label: t('employees'), icon: <Briefcase size={14} /> },
  ];

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<SearchScope, SearchResult[]>);

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: isMobile ? '100%' : '600px' }}>
      {/* Campo de busca */}
      <div style={{ position: 'relative' }}>
        <label htmlFor="global-search-input" style={srOnlyStyle}>
          {placeholder || t('search') || 'Buscar'}
        </label>
        <Search
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999',
            fontSize: '16px',
            pointerEvents: 'none',
          }}
        />
        <input
          ref={inputRef}
          id="global-search-input"
          name="search"
          type="search"
          placeholder={placeholder || t('search') || 'Buscar...'}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onFocus={() => {
            if (searchTerm.trim().length >= 2) {
              setIsOpen(true);
            }
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          autoComplete="off"
          style={{
            padding: '10px 40px 10px 40px',
            border: '1px solid #ddd',
            borderRadius: '24px',
            width: '100%',
            outline: 'none',
            fontSize: '14px',
            transition: 'border-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#2563eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
          }}
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#999',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filtros de escopo */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            padding: '12px',
            border: '1px solid #e5e7eb',
          }}
        >
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {scopeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setSelectedScope(option.value);
                  if (searchTerm.trim()) {
                    handleSearch(searchTerm);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: selectedScope === option.value ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  backgroundColor: selectedScope === option.value ? '#eff6ff' : 'white',
                  color: selectedScope === option.value ? '#2563eb' : '#666',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: selectedScope === option.value ? 600 : 400,
                  transition: 'all 0.2s',
                }}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>

          {/* Resultados */}
          {isSearching ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              {t('searching') || 'Buscando...'}
            </div>
          ) : results.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(groupedResults).map(([type, typeResults]) => (
                <div key={type} style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#666',
                      marginBottom: '8px',
                      padding: '0 8px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {scopeOptions.find(opt => opt.value === type)?.label || type}
                  </div>
                  {typeResults.map((result) => (
                    <div
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          backgroundColor: '#eff6ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#2563eb',
                        }}
                      >
                        {getResultIcon(result.type)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: '#111',
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {result.title}
                        </div>
                        {result.subtitle && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {result.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : searchTerm.trim().length >= 2 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              {t('noResults') || 'Nenhum resultado encontrado'}
            </div>
          ) : null}

          {/* Histórico de buscas */}
          {!isSearching && results.length === 0 && searchHistory.length > 0 && searchTerm.trim().length < 2 && (
            <div>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: '8px',
                  padding: '0 8px',
                  textTransform: 'uppercase',
                }}
              >
                {t('recentSearches') || 'Buscas Recentes'}
              </div>
              {searchHistory.slice(0, 5).map((historyItem, index) => (
                <div
                  key={index}
                  onClick={() => handleSuggestionClick(historyItem)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Clock size={14} color="#999" />
                  <span style={{ fontSize: '14px', color: '#333' }}>{historyItem}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sugestões enquanto digita */}
      {showSuggestions && suggestions.length > 0 && !isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 999,
            border: '1px solid #e5e7eb',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;

